import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminNavbar from './AdminNavbar';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  People as PeopleIcon,
  EventNote as EventIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Pie, Doughnut } from 'react-chartjs-2';
import './AdminModern.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [userFormData, setUserFormData] = useState(null);
  const [guestData, setGuestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const fetchDashboardData = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBase = process.env.REACT_APP_API_URL || '';
      const dashUrl = apiBase.endsWith('/') ? apiBase + 'admin/dashboard' : apiBase + '/admin/dashboard';
      const userFormUrl = apiBase.endsWith('/') ? apiBase + 'admin/user-form-summary' : apiBase + '/admin/user-form-summary';
      const guestUrl = apiBase.endsWith('/') ? apiBase + 'admin/guest-registrations-summary' : apiBase + '/admin/guest-registrations-summary';

      // Fetch dashboard data, user form summary, and guest registrations summary
      const [dashboardResponse, userFormResponse] = await Promise.all([
        axios.get(dashUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        axios.get(userFormUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);

      setDashboardData(dashboardResponse.data);
      setUserFormData(userFormResponse.data);

      // Try to fetch guest data separately to handle potential errors
      try {
        const guestResponse = await axios.get(guestUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setGuestData(guestResponse.data);
      } catch (guestErr) {
        console.error('Error fetching guest data:', guestErr);
        // Set empty guest data if API fails
        setGuestData({
          summary: {
            total_submissions: 0,
            total_guests: 0,
            unique_suppliers: 0,
            avg_guests_per_submission: 0,
            submissions_with_remarks: 0
          },
          recent_submissions: []
        });
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Chart data preparation
  const getAttendanceChartData = () => {
    if (!dashboardData) return null;

    const attending = dashboardData.attendance_summary.attending || 0;
    const notAttending = dashboardData.attendance_summary.not_attending || 0;

    return {
      labels: ['Attending', 'Not Attending'],
      datasets: [
        {
          data: [attending, notAttending],
          backgroundColor: [
            'rgba(0, 184, 148, 0.8)',
            'rgba(214, 48, 49, 0.8)'
          ],
          borderColor: [
            'rgba(0, 184, 148, 1)',
            'rgba(214, 48, 49, 1)'
          ],
          borderWidth: 2,
        },
      ],
    };
  };

  const getResponseRateChartData = () => {
    if (!dashboardData) return null;

    const totalParticipants = dashboardData.csv_summary.total_participants || 0;
    const totalResponses = dashboardData.attendance_summary.total_responses || 0;
    const noResponse = totalParticipants - totalResponses;

    return {
      labels: ['Responses', 'No Response'],
      datasets: [
        {
          data: [totalResponses, noResponse],
          backgroundColor: [
            'rgba(52, 152, 219, 0.8)',
            'rgba(149, 165, 166, 0.8)'
          ],
          borderColor: [
            'rgba(52, 152, 219, 1)',
            'rgba(149, 165, 166, 1)'
          ],
          borderWidth: 2,
        },
      ],
    };
  };

  // Removed unused getUserFormChartData and getSubmissionComparisonChartData to fix no-unused-vars
  // ...existing code...

  const getOverviewBarChartData = () => {
    if (!dashboardData) return null;

    return {
      labels: ['Total Participants', 'Total Responses', 'Attending', 'Not Attending'],
      datasets: [
        {
          label: 'Count',
          data: [
            dashboardData.csv_summary.total_participants || 0,
            dashboardData.attendance_summary.total_responses || 0,
            dashboardData.attendance_summary.attending || 0,
            dashboardData.attendance_summary.not_attending || 0
          ],
          backgroundColor: [
            'rgba(176, 0, 0, 0.8)',
            'rgba(82, 196, 26, 0.8)',
            'rgba(0, 184, 148, 0.8)',
            'rgba(214, 48, 49, 0.8)'
          ],
          borderColor: [
            'rgba(176, 0, 0, 1)',
            'rgba(82, 196, 26, 1)',
            'rgba(0, 184, 148, 1)',
            'rgba(214, 48, 49, 1)'
          ],
          borderWidth: 2,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#333',
          font: {
            size: 14,
            weight: '600'
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#000',
        borderWidth: 2,
        cornerRadius: 8,
        titleFont: {
          size: 14,
          weight: '600'
        },
        bodyFont: {
          size: 13
        },
        padding: 12
      }
    },
    elements: {
      arc: {
        borderWidth: 3
      },
      bar: {
        borderRadius: 6,
        borderSkipped: false
      }
    }
  };

  const pieChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        position: 'bottom',
        labels: {
          color: '#333',
          font: {
            size: 13,
            weight: '600'
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="admin-page-wrapper">
        <div className="admin-bg" />
        <AdminNavbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page-wrapper">
        <div className="admin-bg" />
        <AdminNavbar />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <div className="admin-bg" />
      <AdminNavbar />
      <div className="admin-main-container">
        {dashboardData && (
          <>
            {/* Summary Cards */}
            <div className="admin-glass-card">
            <Typography variant="h5" gutterBottom sx={{ mb: 3, color: '#333', fontWeight: '600', textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                 Event Registration
                </Typography>
              <Grid container spacing={3} className="grid-responsive">
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PeopleIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#b00', mr: 2 }} />
                        <Box>
                          <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            {dashboardData.csv_summary.total_participants || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Participants
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EventIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#52c41a', mr: 2 }} />
                        <Box>
                          <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            {dashboardData.attendance_summary.total_responses || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Responses
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#00b894', mr: 2 }} />
                        <Box>
                          <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            {dashboardData.attendance_summary.attending || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Attending
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CancelIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#d63031', mr: 2 }} />
                        <Box>
                          <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            {dashboardData.attendance_summary.not_attending || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Not Attending
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </div>

            {/* User Form Summary Cards */}
            {userFormData && (
              <div className="admin-glass-card">
                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: '#333', fontWeight: '600', textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  📝 RSVP Form Submissions Summary
                </Typography>
                <Grid container spacing={3} className="grid-responsive">
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon sx={{ fontSize: { xs: 28, sm: 36 }, color: '#9b59b6', mr: 2 }} />
                          <Box>
                            <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.3rem', sm: '1.8rem' } }}>
                              {userFormData.summary.total_submissions || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Submissions
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon sx={{ fontSize: { xs: 28, sm: 36 }, color: '#f39c12', mr: 2 }} />
                          <Box>
                            <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.3rem', sm: '1.8rem' } }}>
                              {userFormData.summary.total_guests || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Guests
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckIcon sx={{ fontSize: { xs: 28, sm: 36 }, color: '#27ae60', mr: 2 }} />
                          <Box>
                            <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.3rem', sm: '1.8rem' } }}>
                              {userFormData.summary.submissions_with_remarks || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              With Remarks
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                
                </Grid>
              </div>
            )}

            {/* Guest Registration Summary Cards */}
            {guestData && guestData.summary && (
              <div className="admin-glass-card">
                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: '#333', fontWeight: '600', textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  👥 Guest Registration Summary
                </Typography>
                <Grid container spacing={3} className="grid-responsive">
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon sx={{ fontSize: { xs: 28, sm: 36 }, color: '#e74c3c', mr: 2 }} />
                          <Box>
                            <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.3rem', sm: '1.8rem' } }}>
                              {guestData.summary.total_submissions || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Submissions
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon sx={{ fontSize: { xs: 28, sm: 36 }, color: '#f39c12', mr: 2 }} />
                          <Box>
                            <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.3rem', sm: '1.8rem' } }}>
                              {guestData.summary.total_guests || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Guests
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckIcon sx={{ fontSize: { xs: 28, sm: 36 }, color: '#27ae60', mr: 2 }} />
                          <Box>
                            <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.3rem', sm: '1.8rem' } }}>
                              {guestData.summary.submissions_with_remarks || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              With Remarks
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  
                </Grid>
              </div>
            )}

            {/* Combined Total Guests Summary */}
            {userFormData && guestData && guestData.summary && (
              <div className="admin-glass-card">
                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: '#333', fontWeight: '600', textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  🎯 Combined Total Guests Summary
                </Typography>
                <Grid container spacing={3} className="grid-responsive">
                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#9b59b6', mr: 2 }} />
                          <Box>
                            <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                              {userFormData.summary.total_guests || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              RSVP Form Guests
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#e74c3c', mr: 2 }} />
                          <Box>
                            <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                              {guestData.summary.total_guests || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Guest Registration Guests
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ background: 'transparent', boxShadow: 'none' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#00b894', mr: 2 }} />
                          <Box>
                            <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                              {Number(userFormData.summary.total_guests || 0) + Number(guestData.summary.total_guests || 0)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              ({Number(userFormData.summary.total_guests || 0)} + {Number(guestData.summary.total_guests || 0)})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Combined Guests
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </div>
            )}

            {/* Charts Section */}
            <div className="admin-glass-card">
              <Typography variant="h5" gutterBottom sx={{ mb: 4, color: '#333', fontWeight: '600', textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                📊 Data Visualization Dashboard
              </Typography>
              <Grid container spacing={4} className="grid-responsive">
                {/* Overview Bar Chart */}
                <Grid item xs={12} sm={6} md={6} lg={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ 
                    height: { xs: 300, sm: 400 }, 
                    p: { xs: 2, sm: 3 }, 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                    borderRadius: 3,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    mb: 3,
                    width: '100%',
                    maxWidth: '100%'
                  }} className="chart-container">
                    <Typography variant="h6" gutterBottom sx={{ mb: 3, color: '#333', fontWeight: '600', textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      📈 Overview Statistics
                    </Typography>
                    <Bar data={getOverviewBarChartData()} options={chartOptions} />
                  </Box>
                </Grid>

                {/* Attendance Pie Chart */}
                <Grid item xs={12} sm={6} md={6} lg={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ 
                    height: { xs: 300, sm: 400 }, 
                    p: { xs: 2, sm: 3 }, 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                    borderRadius: 3,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    mb: 3,
                    width: '100%',
                    maxWidth: '100%'
                  }} className="chart-container">
                    <Typography variant="h6" gutterBottom sx={{ mb: 3, color: '#333', fontWeight: '600', textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      🥧 Attendance Distribution
                    </Typography>
                    <Pie data={getAttendanceChartData()} options={pieChartOptions} />
                  </Box>
                </Grid>

                {/* Response Rate Doughnut Chart */}
                <Grid item xs={12} sm={6} md={6} lg={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ 
                    height: { xs: 300, sm: 400 }, 
                    p: { xs: 2, sm: 3 }, 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                    borderRadius: 3,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    mb: 3,
                    width: '100%',
                    maxWidth: '100%'
                  }} className="chart-container">
                    <Typography variant="h6" gutterBottom sx={{ mb: 3, color: '#333', fontWeight: '600', textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      🍩 Response Rate Analysis
                    </Typography>
                    <Doughnut data={getResponseRateChartData()} options={pieChartOptions} />
                  </Box>
                </Grid>

                {/* Submission Comparison Pie Chart */}
                {/* <Grid item xs={12} sm={6} md={6} lg={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ 
                    height: { xs: 300, sm: 400 }, 
                    p: { xs: 2, sm: 3 }, 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                    borderRadius: 3,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    mb: 3,
                    width: '100%',
                    maxWidth: '100%'
                  }} className="chart-container">
                    <Typography variant="h6" gutterBottom sx={{ mb: 3, color: '#333', fontWeight: '600', textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      📊 RSVP vs User Form Submissions
                    </Typography>
                    <Pie data={getSubmissionComparisonChartData()} options={pieChartOptions} />
                  </Box>
                </Grid> */}

                {/* Detailed Stats */}
                <Grid item xs={12} sm={6} md={6} lg={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ 
                    height: { xs: 300, sm: 400 }, 
                    p: { xs: 2, sm: 3 }, 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                    borderRadius: 3,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    mb: 3,
                    width: '100%',
                    maxWidth: '100%'
                  }} className="chart-container">
                    <Typography variant="h6" gutterBottom sx={{ mb: 4, color: '#333', fontWeight: '600', textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      📋 Detailed Statistics
                    </Typography>
                    
                    {/* Total Responses Progress Bar */}
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontWeight: '600', fontSize: '14px' }}>Total Responses</Typography>
                        <Typography sx={{ fontWeight: '700', color: '#00b894', fontSize: '16px' }}>
                          {dashboardData.attendance_summary.total_responses || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: '100%', 
                        height: 12, 
                        backgroundColor: 'rgba(0, 184, 148, 0.2)', 
                        borderRadius: 6,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          width: `${Math.min((dashboardData.attendance_summary.total_responses / (dashboardData.csv_summary.total_participants || 1)) * 100, 100)}%`, 
                          height: '100%', 
                          backgroundColor: '#00b894',
                          borderRadius: 6,
                          transition: 'width 1s ease-in-out'
                        }} />
                      </Box>
                    </Box>

                    {/* Response Rate Progress Bar */}
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontWeight: '600', fontSize: '14px' }}>Response Rate</Typography>
                        <Typography sx={{ fontWeight: '700', color: '#3498db', fontSize: '16px' }}>
                          {dashboardData.csv_summary.total_participants > 0
                            ? Math.round((dashboardData.attendance_summary.total_responses / dashboardData.csv_summary.total_participants) * 100)
                            : 0}%
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: '100%', 
                        height: 12, 
                        backgroundColor: 'rgba(52, 152, 219, 0.2)', 
                        borderRadius: 6,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          width: `${dashboardData.csv_summary.total_participants > 0
                            ? Math.min((dashboardData.attendance_summary.total_responses / dashboardData.csv_summary.total_participants) * 100, 100)
                            : 0}%`, 
                          height: '100%', 
                          backgroundColor: '#3498db',
                          borderRadius: 6,
                          transition: 'width 1s ease-in-out'
                        }} />
                      </Box>
                    </Box>

                    {/* Attending Rate Progress Bar */}
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontWeight: '600', fontSize: '14px' }}>Attending Rate</Typography>
                        <Typography sx={{ fontWeight: '700', color: '#00b894', fontSize: '16px' }}>
                          {dashboardData.attendance_summary.total_responses > 0
                            ? Math.round((dashboardData.attendance_summary.attending / dashboardData.attendance_summary.total_responses) * 100)
                            : 0}%
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: '100%', 
                        height: 12, 
                        backgroundColor: 'rgba(0, 184, 148, 0.2)', 
                        borderRadius: 6,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          width: `${dashboardData.attendance_summary.total_responses > 0
                            ? Math.min((dashboardData.attendance_summary.attending / dashboardData.attendance_summary.total_responses) * 100, 100)
                            : 0}%`, 
                          height: '100%', 
                          backgroundColor: '#00b894',
                          borderRadius: 6,
                          transition: 'width 1s ease-in-out'
                        }} />
                      </Box>
                    </Box>

                    {/* Not Attending Rate Progress Bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontWeight: '600', fontSize: '14px' }}>Not Attending Rate</Typography>
                        <Typography sx={{ fontWeight: '700', color: '#d63031', fontSize: '16px' }}>
                          {dashboardData.attendance_summary.total_responses > 0
                            ? Math.round((dashboardData.attendance_summary.not_attending / dashboardData.attendance_summary.total_responses) * 100)
                            : 0}%
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        width: '100%', 
                        height: 12, 
                        backgroundColor: 'rgba(214, 48, 49, 0.2)', 
                        borderRadius: 6,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ 
                          width: `${dashboardData.attendance_summary.total_responses > 0
                            ? Math.min((dashboardData.attendance_summary.not_attending / dashboardData.attendance_summary.total_responses) * 100, 100)
                            : 0}%`, 
                          height: '100%', 
                          backgroundColor: '#d63031',
                          borderRadius: 6,
                          transition: 'width 1s ease-in-out'
                        }} />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </div>

            {/* Recent Submissions */}
            <div className="admin-glass-card">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  Recent Submissions
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/admin/submissions')}
                  sx={{
                    backgroundColor: '#000',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#333',
                    }
                  }}
                >
                  View All
                </Button>
              </Box>
              <div className="admin-table-responsive">
                <TableContainer>
                  <Table className="admin-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Reference Number</TableCell>
                        <TableCell>Participant Name</TableCell>
                        <TableCell>Supplier Name</TableCell>
                        <TableCell>Attendance</TableCell>
                        <TableCell>Submitted At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboardData.recent_submissions.map((submission, index) => (
                        <TableRow key={index}>
                          <TableCell>{submission.ref_number}</TableCell>
                          <TableCell>{submission.participant_name}</TableCell>
                          <TableCell>{submission.supplier_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={submission.attend}
                              color={submission.attend === 'Yes' ? 'success' : 'error'}
                              size="small"
                              sx={{ fontWeight: 600, letterSpacing: 1 }}
                            />
                          </TableCell>
                          <TableCell>{formatDate(submission.submitted_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </div>

            {/* Recent User Form Submissions */}
            {userFormData && (
              <div className="admin-glass-card">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    Recent RSVP Form Submissions
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate('/admin/user-form-submissions')}
                    sx={{
                      backgroundColor: '#9b59b6',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#8e44ad',
                      }
                    }}
                  >
                    View All
                  </Button>
                </Box>
                <div className="admin-table-responsive">
                  <TableContainer>
                    <Table className="admin-table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Participant Name</TableCell>
                          <TableCell>Supplier Name</TableCell>
                          <TableCell>Guest Count</TableCell>
                          <TableCell>Submitted At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userFormData.recent_submissions.map((submission, index) => (
                          <TableRow key={index}>
                            <TableCell>{submission.participant_name}</TableCell>
                            <TableCell>{submission.supplier_name}</TableCell>
                            <TableCell>
                              <Chip
                                label={submission.guest_count}
                                color="primary"
                                size="small"
                                sx={{ fontWeight: 600, letterSpacing: 1 }}
                              />
                            </TableCell>
                            <TableCell>{formatDate(submission.submitted_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            )}

            {/* Recent Guest Registrations */}
            {guestData && guestData.recent_submissions && (
              <div className="admin-glass-card">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    Recent Guest Registrations
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate('/admin/guest-registrations')}
                    sx={{
                      backgroundColor: '#e74c3c',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#c0392b',
                      }
                    }}
                  >
                    View All
                  </Button>
                </Box>
                <div className="admin-table-responsive">
                  <TableContainer>
                    <Table className="admin-table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Participant Name</TableCell>
                          <TableCell>Supplier Name</TableCell>
                          <TableCell>Guest Count</TableCell>
                          <TableCell>Submitted At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {guestData.recent_submissions.map((submission, index) => (
                          <TableRow key={index}>
                            <TableCell>{submission.participant_name}</TableCell>
                            <TableCell>{submission.supplier_name}</TableCell>
                            <TableCell>
                              <Chip
                                label={submission.guest_count}
                                color="error"
                                size="small"
                                sx={{ fontWeight: 600, letterSpacing: 1 }}
                              />
                            </TableCell>
                            <TableCell>{formatDate(submission.submitted_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard; 